import React, { memo, useCallback, useState } from "react";

import { twJoin } from "tailwind-merge";

import { FloatingEditorCodeInput } from "src/base-components/EditorTable/FloatingEditorCodeInput";
import { EditorCellProps } from "src/base-components/EditorTable/types";
import {
  ALL_OPERATOR_OPTIONS,
  OperatorDropdown,
} from "src/base-components/OperatorDropdown";
import {
  DecisionTableOperators,
  DecisionTablePredicate,
} from "src/clients/flow-api";
import { isUnaryOperator } from "src/decisionTableNode/ConditionEditor/utils";
import { RowShape } from "src/decisionTableNode/utils";
import { HIGHLIGHT_CLASSES } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { useHighlightedScrollIntoView } from "src/utils/useHighlightedScrollIntoview";

type Props = EditorCellProps<RowShape, DecisionTablePredicate | null>;

const ConditionEditor: React.FC<Props> = ({
  value,
  state,
  cellRef,
  isSearchHighlighted,
  cellId,
  onChange,
}) => {
  const safeValue: DecisionTablePredicate = value ?? {
    id: "",
    operator: DecisionTableOperators.IS_ANY,
    value: "",
  };
  const hasUnaryOperator = isUnaryOperator(safeValue.operator);
  const [ref] = useHighlightedScrollIntoView(isSearchHighlighted);
  const [userValue, setUserValue] = useState(safeValue.value);

  const handleStringChange = useCallback(
    (predicateValue: string) => {
      if (hasUnaryOperator || !value?.id) {
        return;
      }

      if (value.value !== predicateValue) {
        onChange({
          id: value.id,
          operator: value.operator,
          value: predicateValue,
        });
      }
    },
    [hasUnaryOperator, onChange, value?.id, value?.operator, value?.value],
  );

  const handleOperatorChange = useCallback(
    (predicateOperator: string) => {
      if (!value?.id) {
        return;
      }
      const updatedOperator: DecisionTableOperators =
        predicateOperator as unknown as DecisionTableOperators;
      const stringValue = isUnaryOperator(updatedOperator) ? "" : value.value;
      setUserValue(stringValue);
      onChange({
        id: value.id,
        value: stringValue,
        operator: updatedOperator,
      });
    },
    [onChange, value?.value, value?.id],
  );

  const handleEditorBlur = useCallback(() => {
    handleStringChange(userValue);
  }, [userValue, handleStringChange]);

  return (
    <div
      ref={ref}
      className={twJoin(
        "flex h-full min-h-[41px] w-full items-center gap-x-1.5 py-1 pr-2",
        isSearchHighlighted && HIGHLIGHT_CLASSES,
      )}
    >
      <FloatingEditorCodeInput
        cellId={cellId}
        cellRef={cellRef}
        disabled={hasUnaryOperator || state.readonly}
        hidden={hasUnaryOperator}
        isEditing={state.isEditing}
        isSelected={state.isSelected}
        value={userValue}
        onBlur={handleEditorBlur}
        onChange={setUserValue}
      >
        <div onClick={(e) => e.preventDefault()}>
          <OperatorDropdown
            disabled={state.readonly}
            options={ALL_OPERATOR_OPTIONS}
            value={safeValue.operator}
            onChange={handleOperatorChange}
          />
        </div>
      </FloatingEditorCodeInput>
    </div>
  );
};

const MemorizedConditionEditor = memo(
  ConditionEditor,
) as typeof ConditionEditor;

export { MemorizedConditionEditor as ConditionEditor };
